/**
 * Каноничный список пунктов сайдбара/навигации.
 *
 * Единый источник истины для:
 *   - App.tsx (рендер desktop-сайдбара и мобильного меню),
 *   - PanelSettings.tsx (секция «Меню / Сайдбар» — выбор скрытых пунктов).
 *
 * Видимость каждого пункта хранится per-user в серверных prefs
 * (useUserPref → PUT /api/me/prefs) под ключом SIDEBAR_HIDDEN_PREF_KEY как
 * массив id СКРЫТЫХ пунктов. Скрытие чисто визуальное — роуты остаются
 * рабочими по прямой ссылке.
 *
 * `id` — стабильный строковый идентификатор (совпадает с ключами _hasPerm
 * там, где они есть). НЕ переименовывать: id хранятся в prefs клиентов.
 */

export const SIDEBAR_HIDDEN_PREF_KEY = 'sidebar_hidden_items'

export type SidebarNavItem = {
  /** Стабильный id (хранится в prefs). */
  id: string
  /** Подпись в меню. */
  label: string
  /** Пункт из секции «Администрирование» (только VITE_PRODUCT=full). */
  admin?: boolean
  /**
   * Критичный пункт — НЕЛЬЗЯ скрыть. Иначе клиент «запрётся» без доступа к
   * настройкам и не сможет вернуть остальные пункты. Сейчас это «Настройки
   * панели» — раздел, где живёт сам переключатель видимости.
   */
  critical?: boolean
}

export const SIDEBAR_NAV_ITEMS: SidebarNavItem[] = [
  // ── Навигация ──
  { id: 'dashboard', label: 'Рабочий стол' },
  { id: 'projects', label: 'Мои проекты' },
  { id: 'identifiers', label: 'Идентификаторы' },
  { id: 'settings', label: 'Настройки панели', critical: true },
  { id: 'tools', label: 'Инструменты' },
  { id: 'events', label: 'Важные события' },
  { id: 'ideas', label: 'Идеи и предложения' },
  { id: 'bugs', label: 'Баги и ошибки' },
  { id: 'server_orders', label: 'Предзаказ серверов' },
  // ── Администрирование (VITE_PRODUCT=full) ──
  { id: 'licenses', label: 'Лицензии', admin: true },
  { id: 'server_orders_admin', label: 'Аренда серверов', admin: true },
  { id: 'deployments', label: 'Деплой', admin: true },
  { id: 'internal_docs', label: 'Документация', admin: true },
  { id: 'fingerprints', label: 'Отпечатки', admin: true },
  { id: 'ideas_admin', label: 'Идеи (модерация)', admin: true },
  { id: 'bugs_admin', label: 'Баги (модерация)', admin: true },
  { id: 'ai_tickets', label: 'AI-тикеты', admin: true },
  { id: 'alarms', label: 'Новости и уведомления', admin: true },
  { id: 'scenario_editor', label: 'Редактор сценария', admin: true },
]

/** Нормализует значение pref в Set скрытых id (критичные исключаются всегда). */
export function normalizeHiddenSet(raw: unknown): Set<string> {
  const out = new Set<string>()
  if (Array.isArray(raw)) {
    const critical = new Set(SIDEBAR_NAV_ITEMS.filter(i => i.critical).map(i => i.id))
    for (const v of raw) {
      if (typeof v === 'string' && !critical.has(v)) out.add(v)
    }
  }
  return out
}
